import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Box, Stack, Text } from "@mantine/core";
import PvTabOverview from "./PVTabOverview";
import DynamicTabbar from "../../Tarrif/TabBar";
import {
  flattenCountComponentDTOList,
  flattenDesignCountComponent,
  projectDetailsAtom,
} from "../../utils/atoms/projectDetailsAtom";
import { useAtom } from "jotai";
import { getComponentsByType } from "../../utils/atoms/componentFilterUtils";
import { defaultComponentsAtom } from "../../utils/atoms/defaultComponentAtom";
import { componentsAtom } from "../../utils/atoms/componentsAtom";
import { componentDTO } from "../../constants/statusDTO";
import { selectedCurrentComponent } from "../../utils/atoms/selectedComponentAtom";
import { componentStatusAtom } from "../../utils/atoms/componentStatus";
import MGAAPI from "../../api/index";
import { set } from "lodash";
import SEConfirmModal from "../common/SEConfirmModal";
import {
  mgaRegex,
  useAssessmentRouteParams,
  useCurrentDesignComponentLists,
  useCurrentDesignVersion,
  getTrailingNumber,
} from "../../constants/helper";
import displayError from "../../constants/utils";
import { getErrorMessage } from "../../utils/errorUtils";

const MGA_API = new MGAAPI();

const getNextPvTabNumber = (pvComponents = [], fallbackCount = 1) => {
  const componentNumbers = pvComponents
    .map((component) => {
      const componentName = component?.componentName || "";
      const trailing = getTrailingNumber(componentName);
      return trailing ? Number(trailing) : NaN;
    })
    .filter((num) => Number.isFinite(num));

  if (componentNumbers.length === 0) {
    return fallbackCount;
  }

  return Math.max(...componentNumbers) + 1;
};

const PVTabs = (props) => {
    const { t } = useTranslation();
  const {
    form,
    updatePVData,
    setValues,
    handlePvSubmit,
    setLoadingComp,
    loadingComp,
    incentivesData,
  } = props;
  const [activeTab, setActiveTab] = useState(0);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newItemValue, setNewItemValue] = useState("");
  const [tabList, setTabList] = useState([]);
  const [selectedTab, setSelectedTab] = useState({});
  const [projectDetails, setProjectDetails] = useAtom(projectDetailsAtom);
  const selectedDesignAndDesignVersion = useCurrentDesignComponentLists(
    projectDetails?.designComponentsList
  );
  const { projectId, designId, designVersionId } = useAssessmentRouteParams();
  const [defaultComponents] = useAtom(defaultComponentsAtom);
  const [componentDetails, setComponentDetails] = useAtom(componentsAtom);
  const [, setSelectedComponent] = useAtom(selectedCurrentComponent);
  const [, setComponentStatus] = useAtom(componentStatusAtom);
  const pvTabInitializedRef = useRef(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");
  const [tabToDelete, setTabToDelete] = useState({});
  const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState(false);
  const [pendingTabIndex, setPendingTabIndex] = useState(null);
  const selectedVersion = useCurrentDesignVersion(projectDetails?.designWithVersionsDTOList)

  const deleteTab = () => {
    const pvComponents = getComponentsByType(
      selectedDesignAndDesignVersion.countComponentDTOList,
      "PV"
    );

    const lastPv = pvComponents[pvComponents.length - 1];
    if (lastPv?.componentId !== -1) {
      return new Promise((resolve, reject) => {
        setLoadingComp(true);
        if (tabToDelete?.designId) {
          MGA_API.deleteComponent(
            tabToDelete?.designId,
            tabToDelete?.designVersionId,
            tabToDelete?.componentId
          )
            .then((res) => {
              MGA_API.editProject(projectId)
                .then((res) => {
                  setProjectDetails((prev) => ({
                    ...prev,
                    userId: res.userId || "",
                    currencyCode: res?.projectDTO?.currencyCode || "$",
                    projectDTO: res.projectDTO || {},
                    componentDTOList:
                      flattenCountComponentDTOList(res.countComponentDTOList) ||
                      [],
                    designWithVersionsDTOList:
                      res.designWithVersionsDTOList || [],
                    locationDTO: res.locationDTO || {},
                    countComponentDTOList: res.countComponentDTOList,
                    designComponentsList:
                      flattenDesignCountComponent(res?.designComponentsList) ||
                      [],
                  }));
                  setTabList((prevTabList) => {
                    const updatedTabList = prevTabList?.filter(
                      (season) =>
                        season.componentId !== tabToDelete?.componentId
                    );
                    const newActivetabIndex =
                      updatedTabList.length > 0 ? updatedTabList.length - 1 : 0;
                    setActiveTab(newActivetabIndex);
                    setSelectedTab(updatedTabList[newActivetabIndex] || null);
                    handleTabSelectionPromise(newActivetabIndex);
                    return updatedTabList;
                  });
                  setLoadingComp(false);
                  resolve(true);
                })
                .catch((error) => {
                  setLoadingComp(false);
                  console.log("Error editing project:", error);
                  displayError('error', getErrorMessage(error));
                  reject(false); // Reject with false on failure
                });
            })
            .catch((error) => {
              setLoadingComp(false);
              displayError('error', getErrorMessage(error));
              console.log("Error deleting component:", error);
              reject(false); // Reject with false on failure
            });
        } else {
          setLoadingComp(false);
          resolve(false);
        }
      });
    } else {
      removePvFromProjectDetails(tabToDelete?.label);

      setTabList((prevTabList) => {
        const updatedTabList = prevTabList?.filter(
          (season) => season?.tabId !== tabToDelete?.tabId
        );
        const newActivetabIndex =
          updatedTabList.length > 0 ? updatedTabList.length - 1 : 0;
        handleTabSelectionPromise(newActivetabIndex);
        setActiveTab(newActivetabIndex);
        setSelectedTab(updatedTabList[newActivetabIndex] || null);
        return updatedTabList;
      });
    }
  };

  const removePvFromProjectDetails = (pvName) => {
      setProjectDetails((prevProject) => {
        // Remove PV from the correct designVersion's countComponentDTOList and componentDTOList
        const updatedDesignComponentsList = (
          prevProject.designComponentsList || []
        ).map((design) => {
          if (design.designId.toString() === designId) {
            // Update the correct version
            const updatedVersions = (design.designVersionComponents || []).map(
              (version) => {
                if (version.designVersionId.toString() === designVersionId) {
                  // Remove PV from countComponentDTOList
                  let newCountComponentDTOList = Array.isArray(
                    version.countComponentDTOList
                  )
                    ? [...version.countComponentDTOList]
                    : [];
                  const pvEntryIdx = newCountComponentDTOList.findIndex(
                    (item) => item.componentType === "PV"
                  );
                  if (pvEntryIdx !== -1) {
                    const updatedPvList = (
                      newCountComponentDTOList[pvEntryIdx].data || []
                    ).filter(
                      (item) => item?.componentName !== pvName
                    );
                    newCountComponentDTOList[pvEntryIdx] = {
                      ...newCountComponentDTOList[pvEntryIdx],
                      count: updatedPvList.length,
                      data: updatedPvList,
                    };
                  }
                  // Optionally, also remove from componentDTOList if needed (uncomment if required)
                  // let newComponentDTOList = Array.isArray(version.componentDTOList)
                  //   ? version.componentDTOList.filter((item) => item?.componentName !== tabToDelete?.label)
                  //   : [];
                  return {
                    ...version,
                    countComponentDTOList: newCountComponentDTOList,
                    // componentDTOList: newComponentDTOList,
                  };
                }
                return version;
              }
            );
            return {
              ...design,
              designVersionComponents: updatedVersions,
            };
          }
          return design;
        });
        return {
          ...prevProject,
          designComponentsList: updatedDesignComponentsList,
        };
      });
  };

  const handleModalDelete = (tab) => {
    setErrorModalMessage(t('pv.confirmDelete', { name: tab?.label }));
    setTabToDelete(tab);
    setErrorModalOpen(true);
  };

  const handleOldPVComponent = (tab) => {
    setErrorModalMessage(
      t('pv.saveBeforeAdding', { name: tab?.label || tab?.componentName })
    );
    // setTabToDelete(tab);
    setErrorModalOpen(true);
  };

  const addTab = () => {
    const defaultPVData = defaultComponents?.pv || {};
    const latitudeValue = projectDetails?.locationDTO?.latitude;
    const latitude = latitudeValue === null || latitudeValue === undefined || String(latitudeValue).trim() === ""
      ? NaN
      : Number(latitudeValue);
    const defaultTilt = Number.isFinite(latitude) ? Math.abs(latitude).toFixed(2) : "";
    const pvComponents = getComponentsByType(
      selectedDesignAndDesignVersion.countComponentDTOList,
      "PV"
    );
    const lastPv = pvComponents[pvComponents.length - 1] || tabList[0];
    if (lastPv?.designId && lastPv?.componentId !== -1) {
      updatePVData(undefined, undefined, undefined, true);
      const newParameters = {
        componentAttributes: {
          profileFile: [],
          profileDetail: {
            name: "",
            startDate: null,
            startTime: "00:00",
            timeResolution: 15,
            units: "kW",
          },
          relDCPower: "",
          invEfficiency: "95",
          relACPower: "",
          tilt: defaultTilt,
          orientation: "0",
          systemLoss: "0.5",
          powNom: "",
          degradationFactor: "0",
          powNomRef: "",
          axisType: "NO_TRACKING",
        },
        economicAttributes: {
          pvOmCost: "",
          pvCapexCost: "",
          pvLifetime: "",
          // invOmCost: "",
          // invCapexCost: "",
          // invLifetime: "",
        },
      };
      const tabCount = getNextPvTabNumber(pvComponents, (tabList?.length || 0) + 1);
      setComponentDetails((prev) => {
        const newState = { ...prev, currentComponent: "pv" };
        set(newState, `pv.componentName`, `Solar PV ${tabCount}`);
        set(newState, `pv.componentAttributes.componentId`, -1);
        set(
          newState,
          "pv.componentAttributes.pvParameters",
          newParameters?.componentAttributes
        );
        set(
          newState,
          "pv.economicAttributes.pvParameters",
          newParameters?.economicAttributes
        );
        return newState;
      });
      setComponentStatus((prev) => ({
        ...prev,
        pv: { isEditing: true, completed: false },
      }));

      // Add new PV component to countComponentDTOList in projectDetails atom
      // setProjectDetails((prevProject) => {
      //   // Find PV entry or create if not exists
      //   const pvEntryIdx = prevProject.countComponentDTOList?.findIndex(
      //     (item) => item.componentType === "PV"
      //   );
      //   const newComponent = {
      //     designId: null,
      //     designVersionId: null,
      //     componentType: "PV",
      //     componentName: `Solar PV ${tabCount}`,
      //   };
      //   let newCountComponentDTOList = [
      //     ...(prevProject.countComponentDTOList || []),
      //   ];
      //   if (pvEntryIdx !== -1) {
      //     // PV entry exists
      //     newCountComponentDTOList[pvEntryIdx] = {
      //       ...newCountComponentDTOList[pvEntryIdx],
      //       count: (newCountComponentDTOList[pvEntryIdx].count || 0) + 1,
      //       data: [
      //         ...(newCountComponentDTOList[pvEntryIdx].data || []),
      //         newComponent,
      //       ],
      //     };
      //   } else {
      //     // PV entry does not exist
      //     newCountComponentDTOList.push({
      //       componentType: "PV",
      //       count: 1,
      //       data: [newComponent],
      //     });
      //   }
      //   return {
      //     ...prevProject,
      //     countComponentDTOList: newCountComponentDTOList,
      //   };
      // });

      setProjectDetails((prevProject) => {
        // Find the current design
        const updatedDesignComponentsList = (
          prevProject.designComponentsList || []
        ).map((design) => {
          if (design.designId.toString() === designId) {
            // Update the correct version
            const updatedVersions = (design.designVersionComponents || []).map(
              (version) => {
                if (version.designVersionId.toString() === designVersionId) {
                  // Add new PV component to this version's countComponentDTOList and componentDTOList
                  const newComponent = {
                    designId: Number(designId),
                    designVersionId: Number(designVersionId),
                    componentType: "PV",
                    componentId: -1,
                    componentName: `Solar PV ${tabCount}`,
                  };

                  // Update countComponentDTOList
                  let newCountComponentDTOList = Array.isArray(
                    version.countComponentDTOList
                  )
                    ? [...version.countComponentDTOList]
                    : [];
                  const pvEntryIdx = newCountComponentDTOList.findIndex(
                    (item) => item.componentType === "PV"
                  );
                  if (pvEntryIdx !== -1) {
                    // PV entry exists
                    newCountComponentDTOList[pvEntryIdx] = {
                      ...newCountComponentDTOList[pvEntryIdx],
                      count:
                        (newCountComponentDTOList[pvEntryIdx].count || 0) + 1,
                      data: [
                        ...(newCountComponentDTOList[pvEntryIdx].data || []),
                        newComponent,
                      ],
                    };
                  } else {
                    // PV entry does not exist
                    newCountComponentDTOList.push({
                      componentType: "PV",
                      count: 1,
                      data: [newComponent],
                    });
                  }

                  // Update componentDTOList
                  // let newComponentDTOList = Array.isArray(version.componentDTOList)
                  //   ? [...version.componentDTOList]
                  //   : [];
                  // newComponentDTOList.push(newComponent);

                  return {
                    ...version,
                    countComponentDTOList: newCountComponentDTOList,
                    // componentDTOList: newComponentDTOList,
                  };
                }
                return version;
              }
            );
            return {
              ...design,
              designVersionComponents: updatedVersions,
            };
          }
          return design;
        });

        return {
          ...prevProject,
          designComponentsList: updatedDesignComponentsList,
        };
      });

      const newTab = {
        tabId: `solarPv${tabCount}`,
        value: `Solar PV ${tabCount}`,
        label: `Solar PV ${tabCount}`,
        content: (
          <PvTabOverview
            key={`pvTab-${tabCount}-${Date.now()}`}
            updatePVData={updatePVData}
            form={form}
          />
        ),
      };
      setTabList((prevTabs) => {
        const updatedTabList = [...prevTabs, newTab];
        setActiveTab(updatedTabList?.length - 1);
        setSelectedTab(updatedTabList[updatedTabList?.length - 1]);
        return updatedTabList;
      });
    } else {
      handleOldPVComponent(lastPv);
    }
  };
  const handleInputBlur = (index) => {
    const isDuplicate = tabList?.some(
      (tab, idx) => idx !== index && tab.label === newItemValue
    );
    if (isDuplicate) {
      displayError("error", t("pv.error.tabNameExists"));
      setEditingIndex(null);
      return;
    }
    const updatedTabList = [...tabList];
    updatedTabList[index].label = newItemValue;
    setTabList(updatedTabList);
    setComponentDetails((prev) => {
      const newState = { ...prev, currentComponent: "pv" };
      set(newState, `pv.componentName`, newItemValue);

      if (setValues) {
        setValues(newState.pv);
      }
      return newState;
    });
    setEditingIndex(null);
    handlePvSubmit();
  };
  const handleDoubleClick = (index, item) => {
    setEditingIndex(index);
    setNewItemValue(item?.label);
  };

  const isUnsavedPvTab = (tab) => !!tab && (!tab.componentId || tab.componentId === -1);

  const handleTabClick = (index) => {
    if (activeTab === index) return;
    if (form.isTouched() || isUnsavedPvTab(tabList[activeTab])) {
      setPendingTabIndex(index);
      setUnsavedChangesModalOpen(true);
      return;
    }
    return handleTabSelectionPromise(index);
  };

  // OK -> stay on the current tab, keep editing
  const handleUnsavedChangesConfirm = () => {
    setUnsavedChangesModalOpen(false);
    setPendingTabIndex(null);
  };

  // Cancel -> ignore changes and move to the selected tab
  const handleUnsavedChangesCancel = () => {
    setUnsavedChangesModalOpen(false);
    if (pendingTabIndex !== null) {
      const targetIndex = pendingTabIndex;
      setPendingTabIndex(null);
      form.resetTouched();
      setComponentStatus((prev) => ({
        ...prev,
        pv: { completed:true , isEditing: false },
      }));
      const currentTab = tabList[activeTab];
      const removeCurrentTab = isUnsavedPvTab(currentTab);
      if (removeCurrentTab) {
        removePvFromProjectDetails(currentTab?.label);
        setTabList((prevTabs) => prevTabs.filter((tab) => tab?.tabId !== currentTab?.tabId));
      }
      handleTabSelectionPromise(targetIndex);
      if (removeCurrentTab && activeTab < targetIndex) {
        setActiveTab(targetIndex - 1);
      }
    }
  };

  // Save -> save changes and stay on the current tab
  const handleSaveAndStayOnPvTab = () => {
    setUnsavedChangesModalOpen(false);
    setPendingTabIndex(null);
    handlePvSubmit();
    form.resetTouched();
  };

  const handleTabSelectionPromise = (index) => {
    return new Promise((resolve, reject) => {
      try {
        if (tabList[index]?.componentId !== -1) {
          setLoadingComp(true);
          setActiveTab(index);
          const { componentId, designId, designVersionId } = tabList[index];
          MGA_API.getComponentInfo(designId, designVersionId, componentId)
            .then((res) => {
              setSelectedComponent(res);
              setComponentDetails((prevComponents) => {
                const newState = { ...prevComponents };
                set(
                  newState,
                  [componentDTO[res.componentType].toLowerCase()],
                  res
                );

                const trackIsUploadPV =
                  newState?.pv?.uiAttributes?.pvParameters?.isUploadPV;
                if (trackIsUploadPV) {
                  set(
                    newState,
                    "pv.componentAttributes.pvParameters.profileFile",
                    newState?.pv?.componentAttributes?.pvParameters
                      ?.profileFile || []
                  );
                  set(
                    newState,
                    "pv.componentAttributes.pvParameters.createProfileData",
                    []
                  );
                } else {
                  set(
                    newState,
                    "pv.componentAttributes.pvParameters.createProfileData",
                    newState?.pv?.componentAttributes?.pvParameters
                      ?.profileFile || []
                  );
                  set(
                    newState,
                    "pv.componentAttributes.pvParameters.profileFile",
                    []
                  );
                }
                setValues(res);
                form.resetTouched();
                return newState;
              });
              updatePVData(undefined, undefined, undefined, false);
              setSelectedTab(tabList[index]);
              setLoadingComp(false);
              resolve(res);
            })
            .catch((error) => {
              reject(error);
              displayError('error', getErrorMessage(error));
            });
        } else {
          setActiveTab(index);
          setSelectedTab(tabList[index]);
          updatePVData(undefined, undefined, undefined, true);
        }
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleInputChange = (e) => {
    setNewItemValue(e.target.value);
  };

  useEffect(() => {
    // if (pvTabInitializedRef.current) return;
    const pvComponents = getComponentsByType(
      selectedDesignAndDesignVersion.countComponentDTOList,
      "PV"
    );

    if (pvComponents?.length > 0) {
      const tabs = pvComponents?.map((pv, idx) => ({
        tabId: `solarPv${idx + 1}`,
        value: pv?.componentName,
        label: pv?.componentName,
        designId: pv?.designId,
        designVersionId: pv?.designVersionId,
        componentId: pv?.componentId || -1,
        content: (
          <PvTabOverview
            pvData={pv}
            key={`solarPv${idx + 1}`}
            updatePVData={updatePVData}
            form={form}
            loadingComp={loadingComp}
            incentivesData={incentivesData}
            activeTab={activeTab}
          />
        ),
      }));
      setTabList(tabs);
      setSelectedTab(tabs?.[0]);
      pvTabInitializedRef.current = true;
    } else if (!pvTabInitializedRef.current) {
      const tabCount = tabList?.length + 1;
      updatePVData(undefined, undefined, undefined, true);
      setComponentDetails((prev) => {
        const newState = { ...prev, currentComponent: "pv" };
        set(newState, `pv.componentName`, `Solar PV ${tabCount}`);
        set(newState, `pv.componentAttributes.componentId`, -1);
        return newState;
      });
      const newTab = {
        tabId: `solarPv${tabCount}`,
        value: `Solar PV ${tabCount}`,
        label: `Solar PV ${tabCount}`,
        content: (
          <PvTabOverview
            key={`pvTab-${tabCount}-${Date.now()}`}
            updatePVData={updatePVData}
            form={form}
          />
        ),
      };
      setTabList((prevTabs) => {
        if (prevTabs.find((tab) => tab.tabId === newTab.tabId)) return prevTabs;
        setSelectedTab(newTab);
        return [...prevTabs, newTab];
      });
      pvTabInitializedRef.current = true;
    } else if (
      pvComponents?.length === 0 &&
      tabList?.length === 1 &&
      Object.keys(form.errors)?.length > 0
    ) {
      const newTab = {
        tabId: tabList[0]?.tabId,
        value: tabList[0]?.value,
        label: tabList[0]?.label,
        content: (
          <PvTabOverview
            key={`pvTab-${tabList?.length}-${Date.now()}`}
            updatePVData={updatePVData}
            form={form}
          />
        ),
      };
      setTabList(() => {
        const updatedTabList = [newTab];
        setActiveTab(updatedTabList?.length - 1);
        setSelectedTab(updatedTabList[updatedTabList?.length - 1]);
        return updatedTabList;
      });
      pvTabInitializedRef.current = true;
    }
  }, [projectDetails, form.errors]);

  return (
    <>
      <SEConfirmModal
        opened={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        onConfirm={() => {
          deleteTab();
          setErrorModalOpen(false);
        }}
        message={errorModalMessage}
        header={t('common.alert')}
        onlyOk={errorModalMessage?.includes("Please")}
        confirmLabel={t('common.yes')}
        cancelLabel={t('common.no')}
      />
      <SEConfirmModal
        opened={unsavedChangesModalOpen}
        onClose={handleUnsavedChangesCancel}
        onConfirm={handleUnsavedChangesConfirm}
        header={"Warning"}
        confirmSeColor="standard"
        message={
          <Stack spacing={4}>
            <Text>
              {t('pv.unsavedChanges', { name: tabList[activeTab]?.label })}
            </Text>
            <Text>{t('pv.unsavedChanges.okHint')}</Text>
            <Text>{t('pv.unsavedChanges.cancelHint')}</Text>
            <Text>{t('pv.unsavedChanges.saveHint')}</Text>
          </Stack>
        }
        confirmLabel={t('common.ok')}
        cancelLabel={t('common.cancel')}
        onExtra={handleSaveAndStayOnPvTab}
        extraLabel={t('common.save')}
      />
      <div className="mt-2 mb-2" style={{pointerEvents:"auto"}}>
        <DynamicTabbar
          items={tabList}
          activeIndex={activeTab}
          editingIndex={editingIndex}
          newItemValue={newItemValue}
          handleItemClick={handleTabClick}
          handleDoubleClick={handleDoubleClick}
          handleInputChange={handleInputChange}
          handleInputBlur={handleInputBlur}
          removeItem={handleModalDelete}
          addItem={addTab}
          itemKey="tabId"
          itemName="label"
          itemId="tabId"
          tabName="tabName"
          showAddRemove={selectedVersion?.versionStatus !== "COMPLETED"}
          componentType="PV"
        />
      </div>
      <Box style={{ width: "100%", height: "100%" }}>
        {selectedTab?.tabId && (
          <PvTabOverview
            key={`pvTab-${selectedTab.tabId}-${JSON.stringify(
              form.errors[selectedTab.label] || {}
            )}-${JSON.stringify(form.values[selectedTab.label] || {})}`}
            pvData={selectedTab}
            updatePVData={updatePVData}
            form={form}
            loadingComp={loadingComp}
            incentivesData={incentivesData}
            activeTab={activeTab}
          />
        )}
      </Box>
    </>
  );
};

export default PVTabs;
